import {
  KatAlert, KatDivider,
  KatIcon,
  KatModal,
  KatSpinner,
} from '@amzn/katal-react';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CategoryCard from 'src/components/homepage/CategoryCard';
import CategoryCardLegacy from 'src/components/homepage/CategoryCardLegacy';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { fetchCategories } from 'src/redux/slices/threadCategories';
import { AppState } from 'src/redux/store';
import FetchStatus from 'src/types/FetchStatus';
import { isStatusLoading, useCheckWeblab } from 'src/utils';
import WeblabId from 'src/weblab/weblabId';
import { DiscussionCategoryContainerLayout } from '../layouts/homepage/DiscussionCategoryContainer';

const DiscussionCategory = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const categories = useAppSelector((state: AppState) => state.threadCategories.categories);
  const categoryList = Object.values(categories);
  const categoriesStatus = useAppSelector((state: AppState) => state.threadCategories.status);
  const [displayModal, setDisplayModal] = useState(false);
  const isAdaptive = useCheckWeblab(WeblabId.SELLER_FORUMS_HOMEPAGE_ADAPTIVE);

  useEffect(() => {
    if (categoriesStatus !== FetchStatus.complete) dispatch(fetchCategories());
  }, []);

  if (isStatusLoading(categoriesStatus)) return <KatSpinner data-testid="discussion-category-spinner" size="small" />;

  if (categoriesStatus === FetchStatus.failed) {
    return (
      <KatAlert
        data-testid="discussion-category-alert"
        persistent
        className="my-6"
        variant="warning"
        description={t('request_homePageCategoriesFetchFailureDescription')}
        header={t('request_homePageCategoriesFetchFailureHeader')}
      />
    );
  }

  const homepageCategoryDescription = t('homePage_categoryDescription');
  const homepageCategoryHeader = t('homePage_categoryHeader');
  const categoryCards = categoryList.map(({
    nameStringId,
    id: categoryId,
  }) => (
    /**
     * @todo To fetch discussion count and last activity time
     * sim - https://sim.amazon.com/issues/SPCE-3275
     */
    isAdaptive
      ? (
        <CategoryCard
          key={categoryId}
          categoryId={categoryId}
          nameStringId={nameStringId}
        />
      )
      : (
        <CategoryCardLegacy
          key={categoryId}
          categoryId={categoryId}
          nameStringId={nameStringId}
        />
      )
  ));

  if (isAdaptive) {
    return (
      <DiscussionCategoryContainerLayout
        cards={categoryCards}
        button={(
          <div data-testid="discussion-categories" className="border-kat-zircon bg-kat-white hover:border-kat-jungle-mist">
            <button
              type="button"
              data-testid="discussion-categories-link"
              className="w-full border-none bg-transparent pt-[26px] pb-[22px] font-medium no-underline shadow-none hover:cursor-pointer"
              onClick={() => setDisplayModal(true)}
            >
              <div className="flex h-full flex-col justify-between">
                <div className="flex flex-row">
                  <KatIcon
                    className="mt-px mr-2 text-kat-nordic"
                    name="dashboard"
                    size="small"
                  />
                  <h3
                    slot="trigger"
                    className="text-[18px] leading-[1.25rem]"
                  >
                    {homepageCategoryHeader}
                  </h3>
                  <KatIcon
                    className="mt-px ml-auto text-kat-eastern"
                    name="arrow_forward"
                    size="small"
                  />
                </div>
                <span className="text-left font-normal">
                  {homepageCategoryDescription}
                </span>
              </div>
            </button>
          </div>
      )}
        modal={(
          <KatModal
            data-testid="discussion-categories-modal"
            title={homepageCategoryHeader}
            visible={displayModal}
            onClose={() => setDisplayModal(false)}
            className="fullscreen-kat-modal"
          >
            {/* The line below ensures the modal fills the screen horizontally. */}
            <div className="w-screen" />
            {categoryCards.map((
              categoryCard: ReactElement,
              index: number,
            ) => (
              <div className="flex flex-col gap-[18px]" key={categoryCard.props.categoryId}>
                {index > 0 && <KatDivider className="my-0 mx-4 bg-kat-seattle" />}
                {categoryCard}
              </div>
            ))}
            <div slot="footer" />
          </KatModal>
      )}
        homepageCategoryHeader={homepageCategoryHeader}
      />
    );
  }

  return (
    <div
      data-testid="discussion-categories"
      className="mt-3.5 mb-[26px] border border-solid border-y-kat-seattle border-x-transparent bg-kat-azure pt-[22px] pb-6.5"
    >
      <div className="container">
        <div className="kat-col-lg-8 kat-offset-lg-2">
          <h3 className="mb-3.5">{t('homePage_categoryHeader')}</h3>
          <div className="grid grid-cols-1 gap-x-4.5 gap-y-2 sm:grid-cols-2">
            {categoryCards}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscussionCategory;
